import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type SortDirection = "asc" | "desc";

const PAGE_KEY = "page";
const SORT_KEY = "sort";
const DIR_KEY = "dir";

// Keeps a list's page, sort and applied filters in the URL query string so that
// Back, refresh and shared links restore exactly what the user was looking at.
// Values equal to their defaults are left out of the URL to keep it short.
// Call only one setter per event: each one rewrites the query string from the
// current render, so a second call in the same handler would undo the first.
export function useListParams<F extends Record<string, string>, S extends string = string>(
  emptyFilters: F,
  defaultSort?: { field: S; direction: SortDirection },
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageNumber = Math.max(1, parseInt(searchParams.get(PAGE_KEY) || "1", 10) || 1);
  const sortField = (searchParams.get(SORT_KEY) || defaultSort?.field || "") as S;
  const dirParam = searchParams.get(DIR_KEY);
  const sortDirection: SortDirection =
    dirParam === "asc" || dirParam === "desc" ? dirParam : defaultSort?.direction ?? "asc";

  const filterKeys = Object.keys(emptyFilters);
  const filtersKey = filterKeys.map((key) => searchParams.get(key) ?? "").join("\u0000");

  const appliedFilters = useMemo(() => {
    const values = filtersKey.split("\u0000");
    return filterKeys.reduce(
      (acc, key, i) => ({ ...acc, [key]: values[i] ?? "" }),
      {} as Record<string, string>,
    ) as F;
    // filterKeys is derived from a module-level constant in every caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPageNumber = useCallback(
    (page: number) =>
      update((params) => {
        if (page <= 1) params.delete(PAGE_KEY);
        else params.set(PAGE_KEY, String(page));
      }),
    [update],
  );

  const setSort = useCallback(
    (field: S, direction: SortDirection) =>
      update((params) => {
        if (defaultSort && field === defaultSort.field) params.delete(SORT_KEY);
        else params.set(SORT_KEY, field);
        if (defaultSort && direction === defaultSort.direction) params.delete(DIR_KEY);
        else params.set(DIR_KEY, direction);
        params.delete(PAGE_KEY);
      }),
    [update, defaultSort],
  );

  // Applying filters always starts again from the first page.
  const setAppliedFilters = useCallback(
    (filters: F) =>
      update((params) => {
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.set(key, value);
          else params.delete(key);
        });
        params.delete(PAGE_KEY);
      }),
    [update],
  );

  return { pageNumber, setPageNumber, sortField, sortDirection, setSort, appliedFilters, setAppliedFilters };
}
